<template src="./reviews-summary.html"></template>

<script lang="ts">
import { defineAsyncComponent, nextTick } from 'vue'
import { callTurnToConfigFn } from 'util/turnto-utils'
import { CDN_URL } from 'services/http-client'
import domUtils from 'util/dom-utils'
import { hostsConfig, turnToConfig } from 'init/config'
import globalStore from 'init/global-store'
import GridLayoutDataMixin from 'layouts/grid/GridLayoutDataMixin.vue'
import loadJs from 'util/browser/load-js'
import launchWriteReview from 'util/launch-write-review'
import { localizedThousandsInteger, localizedRating } from 'util/localization-utils'
import logger from 'util/logger'
import { getMediaList } from 'services/media-service'
import reviewsListConfig from 'widgets/reviews-list/reviews-list-widget.config'
import reviewsStore, { MIN_REVIEWS_FOR_TOOLBAR } from 'store/reviews-store'
import reviewsSummaryConfig from './reviews-summary.config'
import reviewsSummaryService from 'services/api/reviews-summary'
import reviewsSummaryStore from 'store/reviews-summary-store'
import submissionStore from 'store/submission-store'
import ActionableRatingStars from 'components/rating/ActionableRatingStars.vue'
import GridLayout from 'layouts/grid/GridLayout.vue'
import { RestError, SummaryDimension } from 'services/api-service'
import SummaryDimRating from 'components/reviews-summary/SummaryDimRating.vue'
import SummaryDimRange from 'components/reviews-summary/SummaryDimRange.vue'
import SummaryDimSelect from 'components/reviews-summary/SummaryDimSelect.vue'
import SummaryDimSelectImage from 'components/reviews-summary/SummaryDimSelectImage.vue'
import RatingsBreakdown from 'components/reviews-summary/RatingsBreakdown.vue'
import RatingStars from 'components/rating/RatingStars.vue'
import trackingService from 'services/tracking-service'
import UserToolbar from 'components/user-toolbar/UserToolbar.vue'


const JSON_LOADER_ID = 'tt-agg-json-ld'

// Needing to explicitly include here because for some reason Webpack is not using the Promise
// provided plugin need for a particular case here.  When the gallery is included as a widget
// on the same page where the gallery is included in the reviews summary, it is using a Promise
// to get the already included gallery component.  In other words, it does not need to be
// dynamically loaded since it is already included in the gallery widget.  This causes a Promise
// to be used in this component.  This is fine for most browsers that have a native Promise, but
// not for IE11 and such.  Part of TT-11452 fix.
// eslint-disable-next-line:variable-name
// const Promise = window['TurnTo'].promise

export default {
  data() {
    return {
      customDimensions: [],
      catItem: null,
      dataIsLoaded: false,
      disableFiltering: false,
      isGalleryHidden: false, // only to be used in hasHiddenForDisplay computed propery
      globalStoreState: globalStore.state,
      previousSku: '',
      rating: 0.0,
      ratingFormatted: '',
      ratingsBreakdown: [],
      recommendedPercentageFormatted: '',
      ratingsCount: 0,
      reviewsCount: 0,
      submissionStoreState: submissionStore.state,
      showDetailsClicked: false
    }
  },
  components: {
    'tt-actionable-rating-stars': ActionableRatingStars,
    'tt-ratings-breakdown': RatingsBreakdown,
    'tt-rating-stars': RatingStars,
    'tt-grid-layout': GridLayout,
    'tt-summary-custom-rating': SummaryDimRating,
    'tt-summary-custom-range': SummaryDimRange,
    'tt-summary-custom-select': SummaryDimSelect,
    'tt-summary-custom-select-image': SummaryDimSelectImage,
    'tt-gallery':  defineAsyncComponent(
      // tslint:disable-next-line
      () => import(/* webpackChunkName: 'gallery' */ 'components/gallery/Gallery.vue')
    ),
    'tt-spectrum':  defineAsyncComponent(
      // tslint:disable-next-line
      () => import(/* webpackChunkName: 'spectrum' */ 'components/spectrum/Spectrum.vue')
    ),
    'tt-user-toolbar': UserToolbar
  },
  mixins: [GridLayoutDataMixin],
  computed: {
    aggregateAttributes() {
      if (this.aggregateMetaOn && this.ratingsCount > 0) {
        return {
          itemprop: 'aggregateRating',
          itemscope: true,
          itemtype: 'http://schema.org/AggregateRating'
        }
      }
      return {}
    },
    aggregateMetaOn() {
      return reviewsSummaryConfig.aggregateMetaOn
    },
    currentSku(): string {
      return this.globalStoreState.sku
    },
    displayableDimensions() {
      return this.customDimensions.filter((dim, i) => {
        const slotName = `customDim-${(i+1)}`
        dim.slotName = slotName
        return !this.isSlotHidden(slotName)
      })
    },
    dimFiltersOn() {
      return reviewsSummaryConfig.dimFiltersOn && !this.disableFiltering
    },
    displayShowDetails() {
      return !this.showDetailsClicked && ~this.allItemsInLayout.indexOf('showDetails') &&
        this.hasHiddenForDisplay
    },
    formattedRatingCount() {
      return localizedThousandsInteger(this.ratingsCount)
    },
    galleryConfig() {
      return reviewsSummaryConfig.childWidgets['gallery']
    },
    hasGallery() {
      return ~this.allItemsInLayout.indexOf('gallery')
    },
    hasSpectrum() {
      return ~this.allItemsInLayout.indexOf('spectrum')
    },
    // this determines whether any of the hidden items (after "show details") actually have data
    // for display. For example, customDim-5 should not be included if there are only 4 custom dims.
    // Used to determine whether or not "show details" should even display.
    // Just need to know that at least one of them does.
    hasHiddenForDisplay() {
      return this.slotNamesAfterShowDetails.find((slotName) => {
        if (slotName === 'recommend') {
          return this.recommendedPercentageFormatted
        } else if (slotName.indexOf('customDim') === 0) {
          const dimNum = parseInt(slotName.split('-')[1], 10)
          return this.customDimensions.length >= dimNum
        } else if (slotName === 'spectrum') {
          return this.reviewsCount > 1 // TODOL is this correct?
        } else if (slotName !== 'gallery') {
          // all other items should have data.  Returning true here to avoid having to make call
          // to see if gallery has media or not
          return true
        } else if (slotName === 'gallery') {
          if (this.isGalleryHidden) {
            return true
          }
          // checking to see if there are any images in the gallery to be displayed
          getMediaList([this.currentSku], [], 0, this.galleryConfig)
            .then(data => this.isGalleryHidden = data.media.length > 0).catch(() => {
              // do nothing, having catch prevents console error when there is a failure
            })
          // return false now that check has been kicked-off.  isGalleryHidden will change to
          // true if there actually are images.
          return false
        }
      }) ? true : false
    },
    headingLevel() {
      return reviewsSummaryConfig.headingLevel
    },
    isLaunchingWriteReview() {
      return this.submissionStoreState.isLaunchingWriteReview
    },
    showRangeMiddle() {
      return reviewsSummaryConfig.showRangeMiddle
    },
    spectrumConfig() {
      return reviewsSummaryConfig.childWidgets['spectrum']
    }
  },
  methods: {
    fetchData() {
      if (this.currentSku) {
        const isFirstCall = this.currentSku !== this.previousSku
        if (isFirstCall) {
          trackingService.queueEvent('reviews-summary', 'load', this.currentSku)
        }
        reviewsSummaryService.reviewsSummary({
          sku: this.currentSku
        }).then((data) => {
          if (!data) { // invalid sku most likely
            return
          }
          this.previousSku = this.currentSku
          this.catItem = data.catItem
          this.ratingsCount = data.reviewsCount || 0 // actually "ratings" count
          this.reviewsCount = data.directReviewsCount + data.relatedReviewsCount
          this.disableFiltering = reviewsListConfig.relatedReviewsDisplay === 'INCLUDED' ?
            this.reviewsCount < MIN_REVIEWS_FOR_TOOLBAR :
            data.directReviewsCount < MIN_REVIEWS_FOR_TOOLBAR
          this.recommendedPercentageFormatted = data.recommendedPercentageFormatted
          this.rating = data.rating
          this.ratingFormatted = data.ratingFormatted
          this.ratingsBreakdown = data.ratingsBreakdown
          this.customDimensions = data.customDimensions
          this.dataIsLoaded = !data.hideDisplay
          nextTick(() => { callTurnToConfigFn('reviewsSummary', 'onFinish') })
        }).catch((e: RestError) => {
          if (e.errorCode === 'turnto-offline') {
            logger.info('TurnTo is in offline mode - Reviews Summary not loaded', e)
          } else {
            logger.warn('Error loading data for the Reviews Summary widget', e)
          }
        })
      }
    },
    getFormattedRating(average) {
      return localizedRating(average)
    },
    isSlotHidden(slotName) {
      return !this.showDetailsClicked && ~this.slotNamesAfterShowDetails.indexOf(slotName)
    },
    loadJsonLd() {
      if (globalStore.sku && !this.aggregateMetaOn
        && reviewsSummaryConfig.aggregateJsonLdMetaOn) {
        loadJs(
          `${CDN_URL}/sitedata/${hostsConfig.siteKey}/${globalStore.sku}` +
            `/d/jsonld/${turnToConfig.locale}/${reviewsSummaryStore.schemaOrgEntityType}`,
          JSON_LOADER_ID,
          true,
          false
        ).catch(e => logger.warn('Error loading json-ld', e))
      }
    },
    onShowDetailsClick() {
      this.showDetailsClicked = true
    },
    setFilter(group: string, name: string, value, dim: SummaryDimension) {
      reviewsStore.applySingleFilter(group, name, value, dim)
      nextTick(() => {
        // even with nextTick, the heading isn't always updated right away.  Delaying to give it
        // time to be updated before focusing so it reads the correct count
        setTimeout(() => {
          // not great to so tightly couple the class name, but simpler than firing event to focus
          domUtils.safeFocus(
            document.querySelector('#tt-reviews-list .tt-c-reviews-list__heading') as HTMLElement)
        }, 1000)
      })
    },
    writeReview(rating) {
      launchWriteReview(rating)
    }
  },
  watch: {
    //  whenever the currentSku changes, we need to reload the summary data
    currentSku() {
      this.fetchData()
      // TODO: believe the returning script will need to overwrite any existing json-ld, but
      // not sure that works or not
      this.loadJsonLd()
    },
    currentLayout(newLayout, oldLayout) {
      // something is causing this to be called when the layout hasn't changed
      // so checking just in case
      if (newLayout !== oldLayout) {
        this.showDetailsClicked = false
      }
    }
  },
  created() {
    // set the layouts configuration.  This is a method in the GridLayoutDataMixin
    this.setLayoutsConfig(reviewsSummaryConfig.breakpoints)
    this.fetchData()
    this.loadJsonLd()
  }
}
</script>

<style lang="scss" src="./reviews-summary.scss"></style>
